/**
 * 4 ステップ progress stepper（Consent → Language → Recording → Done）。
 *
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business 画面遷移の進捗を視覚化する stepper。アクセス時の文脈と達成感を提供する
 * @graph-connects none
 */

import type { ReactNode } from 'react';

export interface StepperStep {
  id: string;
  label: string;
}

interface StepperProps {
  steps: readonly StepperStep[];
  currentIndex: number;
}

/**
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business stepper 本体。完了 / アクティブ / 未到達の 3 状態を視覚的に区別
 * @graph-connects none
 */
export function Stepper({ steps, currentIndex }: StepperProps): ReactNode {
  return (
    <ol className="mx-auto flex w-full max-w-2xl items-center gap-2 text-xs">
      {steps.map((step, idx) => {
        const state = idx < currentIndex ? 'done' : idx === currentIndex ? 'active' : 'pending';
        return (
          <li key={step.id} className="flex flex-1 items-center gap-2">
            <span
              aria-hidden
              className={[
                'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold ring-1 transition-all duration-[var(--duration-base)]',
                state === 'done'
                  ? 'bg-[var(--color-accent)] text-[var(--color-bg-base)] ring-[var(--color-accent)]'
                  : state === 'active'
                    ? 'bg-[var(--color-bg-elevated)] text-[var(--color-accent)] ring-[var(--color-accent)] shadow-[0_0_0_4px_var(--color-accent-soft)]'
                    : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] ring-[var(--color-border)]',
              ].join(' ')}
            >
              {state === 'done' ? '✓' : idx + 1}
            </span>
            <span
              className={[
                'hidden text-xs font-medium tracking-wide sm:inline',
                state === 'pending'
                  ? 'text-[var(--color-text-muted)]'
                  : 'text-[var(--color-text-primary)]',
              ].join(' ')}
            >
              {step.label}
            </span>
            {idx < steps.length - 1 ? (
              <span
                aria-hidden
                className={[
                  'mx-1 h-px flex-1 transition-colors',
                  state === 'done' ? 'bg-[var(--color-accent)]' : 'bg-[var(--color-border)]',
                ].join(' ')}
              />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
